import React from 'react';
import { Activity, TrendingUp, TrendingDown } from 'lucide-react';

const BAND_STYLES = {
    strong: { ring: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500' },
    healthy: { ring: 'border-[#55C5D0] bg-[#f0fafb]', text: 'text-[#3a99a3]', bar: 'bg-[#55C5D0]' },
    slowing: { ring: 'border-amber-200 bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-500' },
    at_risk: { ring: 'border-red-200 bg-red-50', text: 'text-red-700', bar: 'bg-red-500' },
};

const COMPONENT_LABELS = {
    traffic: 'Buyer traffic',
    unique: 'Unique buyers',
    repeat: 'Repeat activity',
    enquiries: 'Enquiries',
    saves: 'Saves',
    ofi_attend: 'Open home attendance',
    offers: 'Offers',
    recency: 'Recent activity',
    momentum: 'Momentum',
};

/**
 * Campaign Health Score with the reason it landed there.
 * The score is only useful if it says WHY, so the reason line is not optional.
 */
export default function CampaignHealthCard({ health, momentumPct }) {
    if (!health) return null;
    const style = BAND_STYLES[health.band] || BAND_STYLES.healthy;
    const momentum = momentumPct ?? health.momentum_pct;

    return (
        <div className={`rounded-2xl border p-5 ${style.ring}`}>
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-baseline gap-2">
                    <span className={`text-4xl font-extrabold ${style.text}`}>{health.score}</span>
                    <span className="text-sm font-semibold text-gray-500">/100</span>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <Activity size={16} className={style.text} />
                        <span className={`text-sm font-bold uppercase tracking-wide ${style.text}`}>
                            Campaign health: {health.label}
                        </span>
                        {momentum !== null && momentum !== undefined && (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${
                                momentum >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                            }`}>
                                {momentum >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                {Math.abs(momentum).toFixed(0)}% week on week
                            </span>
                        )}
                    </div>
                    <p className="mt-1 text-sm text-[#374151]">{health.reason}</p>
                </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-5">
                {Object.entries(health.components || {}).map(([key, points]) => (
                    <div key={key}>
                        <div className="flex items-center justify-between text-[11px] font-semibold text-gray-500">
                            <span>{COMPONENT_LABELS[key] || key}</span>
                            <span>{points}</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-white/70">
                            <div
                                className={`h-1.5 rounded-full ${style.bar}`}
                                style={{ width: `${Math.min(100, (points / 18) * 100)}%` }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
